import { ArrowBack } from '@mui/icons-material';
import { Box, Dialog, IconButton, Skeleton, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import React, { useEffect, useState } from 'react'
import SelectDatePicker, { AutoCompleteSelect } from './CustomSelectFields';
import { getJobRoles, getTags, getTagById } from 'services/tagService';
import { useServices } from 'contexts/SingleSessionProvider';
import CustomButton, { CustomLoadingButton } from './CustomButtons';
import TextField, { TextArea } from './CustomFields';
import { SimpleSelect } from './CustomSelectFields';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { getBatches } from 'services';
import { Session } from 'utils';
import MockDatePicker from './PersonalizedInterview/MockDatePicker';
import { createShortlistTask } from 'services/shortlistingTask';
import { useSnackbar } from 'contexts';

const useStyles = makeStyles((theme) => ({
  container: {
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
    display: 'flex',
    flexDirection : 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  box: {
    padding: theme.spacing(10, 15),
    display: 'flex',
    gap: theme.spacing(6),
    flexDirection: 'column',
    width: '700px',
    background: '#FFFFFF',
    borderRadius: theme.spacing(2),
    boxShadow: `0px 4px 4px 0px #CCD4DE1A,
                0px 10px 20px 0px #CCD4DE4D,
                0px 7px 28px 0px #CCD4DE99`,
  },
  hoverButton: {
    marginRight : '16px',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  borderBottom: {
    borderBottom: `1px solid ${theme.palette.neutral['clr-400']}`,
    paddingBottom: theme.spacing(5),
  },
  headBox: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
}))

const STEPS = {
  JOB_ROLE_COMPANY_SELECTION: 'jobRoleCompanySelection',
  JOB_DESCRIPTION: 'jobDescription',
  PUBLISH_TASK: 'publishTask'
}

export const Header = ({ onBack, title }) => {
  const classes = useStyles();
  return (
    <Box className={`${classes.borderBottom} ${classes.headBox}`}>
        <ArrowBack
          onClick={onBack}
          className={classes.hoverButton}
          sx={{ color: '#1961BF' }}
        />
      <Typography variant="h4-medium">
        {title}
      </Typography>
    </Box>
  );
};

const AutoCompleteSelectSkeleton = () => (
    <Box sx={{ width: '100%', height: '52px', mb: 2 }}>
      <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 1 }} />
    </Box>
  )

export const PublishingComponent = ({ onPublish, loading , editPage = false , initialValues = {
  name: '',
  batch: '',
  publishOn: null,
  expiresOn: null,
} }) => {
  const classes = useStyles();
  const [options, setOptions] = useState([]);

  useEffect(() => {
    getBatches({ client: Session.userId })
      .then(({ batches }) => setOptions(batches))
      .catch(console.error);
  }, []);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Shortlisting task name is required'),
    batch: Yup.string().required('Please select a batch'),
    publishOn: Yup.date().required('Publish date is required'),
    expiresOn: Yup.date()
      .required('Expiry date is required')
      .min(
        Yup.ref('publishOn'),
        'Expiry date must be after publish date'
      ),
  });

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema,
    onSubmit: (values) => {
      onPublish && onPublish(values);
    },
  });

  const handleDateChange = React.useCallback((name, date) => {
    formik.setFieldValue(name, date);
  }, [formik]);

  const { batch, expiresOn, name, publishOn } = formik.values;
  const isPublishDisabled = !formik.isValid || !batch || !expiresOn || !name || !publishOn;

  return (
    <Box>

      {/* Publishing Form */}
      <form onSubmit={formik.handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <TextField
          label="Shortlisting task name"
          name="name"
          placeholder="Enter task name"
          value={formik.values.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          background='#FFFFFF'
          border='1px solid #636F7F'
          width='100%'
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
        />

        <SimpleSelect
          label="Publishing in Batch"
          name="batch"
          options={options}
          value={formik.values.batch}
          onChange={formik.handleChange}
          error={formik.touched.batch && Boolean(formik.errors.batch)}
          helperText={formik.touched.batch && formik.errors.batch}
          style={{ width: '100%' }}
          disabled={editPage}
        />

        <MockDatePicker
          name="publishOn"
          label="Publish on"
          value={formik.values.publishOn}
          onChange={handleDateChange}
          disabled={false}
        />

        <MockDatePicker
          name="expiresOn"
          label="Expires on"
          value={formik.values.expiresOn}
          onChange={handleDateChange}
          disabled={!formik.values.publishOn}
          minDate={formik.values.publishOn}
        />

        <CustomLoadingButton
          type="submit"
          disabled={isPublishDisabled}
          style={{ width: '100%' }}
          size='medium'
          loading={loading}
        >
          Publish Task
        </CustomLoadingButton>
      </form>
    </Box>
  );
};

const CreateShortListTask = ({open , onClose , jobRoleSpecific = false , setRefresh}) => {
    const classes = useStyles();
    const service = useServices();
    const snackbar = useSnackbar();

    const [jobRoles, setJobRoles] = useState([])
    const [companies , setCompanies] = useState([])
    const [loading , setLoading] = useState(false)
    const [companiesLoading, setCompaniesLoading] = useState(false)
    const [detailsPresent , setDetailsPresent] = useState(true)
    const [currentStep, setCurrentStep] = useState(STEPS.JOB_ROLE_COMPANY_SELECTION)
    const [formData, setFormData] = useState({
        jobRole: '',
        companyName: '',
        jobDescription: '',
      })

    const getStepFlow = () => {
      if (jobRoleSpecific) {
        return [STEPS.JOB_ROLE_COMPANY_SELECTION, STEPS.PUBLISH_TASK];
      } else {
        return [STEPS.JOB_ROLE_COMPANY_SELECTION, STEPS.JOB_DESCRIPTION, STEPS.PUBLISH_TASK];
      }
    }

    const stepFlow = getStepFlow();
    const currentStepIndex = stepFlow.indexOf(currentStep);

    useEffect(() => {
      setCurrentStep(STEPS.JOB_ROLE_COMPANY_SELECTION)
      setFormData({})
      setDetailsPresent(true)
    },[open])

    useEffect(() => {
        const fetchDetails = async () => {
          try {
            setLoading(true)
            
            if (jobRoleSpecific) {
              const tagFilter = {
                category: "jobRole",
                include: ['domain']
              };
              const fetchedJobRoles = await service.callService(getTags, tagFilter);
              const filteredJobRoles = fetchedJobRoles.tags
                .filter(tag => tag.domain)
                .sort((a, b) => a.code.localeCompare(b.code))
                .reduce((uniqueJobRoles, currentTag) => {
                  if (!uniqueJobRoles.some(existingTag => existingTag.code === currentTag.code)) {
                    return [...uniqueJobRoles, { 
                      ...currentTag, 
                      _id: currentTag.code,
                      name: currentTag.name 
                    }];
                  }
                  return uniqueJobRoles;
                }, []);
              
              setJobRoles(filteredJobRoles);
            } else {
              const fetchedMappedJobRoles = await service.callService(getJobRoles);
              const sortedJobRoles = Object.values(fetchedMappedJobRoles)
                .sort((a, b) => a.name.localeCompare(b.name));
              setJobRoles(sortedJobRoles);
            }
          } catch (error) {
            console.error('Error fetching interview details:', error);
          } finally {
            setLoading(false);
          }
        };
    
        fetchDetails();
      }, []);

      useEffect(() => {
        const setCompaniesFromJobRole = () => {
          if (formData.jobRole?.companies && !jobRoleSpecific) {
            setCompaniesLoading(true)
            setCompanies(formData.jobRole.companies);
            setCompaniesLoading(false)
          } else if (!formData.jobRole && !jobRoleSpecific) {
            setCompanies([])
            setCompaniesLoading(false)
          }
        };
    
        setCompaniesFromJobRole();
      }, [formData.jobRole]);

      useEffect(() => {
        const setJdFromCompany = async () => {
          if (formData.companyName && !jobRoleSpecific && detailsPresent) {
            if (formData?.companyName?.jobDescriptions?._id) {
              try {
                const jobDescriptionData = await service.callService(getTagById, formData.companyName.jobDescriptions._id);
                if (jobDescriptionData?.content) {
                  handleInputChange('jobDescription', jobDescriptionData.content);
                } else {
                  handleInputChange('jobDescription', formData?.companyName?.jobDescriptions?.content || '');
                }
              } catch (error) {
                console.error('Error fetching job description:', error);
                handleInputChange('jobDescription', formData?.companyName?.jobDescriptions?.content || '');
              }
            } else {
              handleInputChange('jobDescription', formData?.companyName?.jobDescriptions?.content || '');
            }
          } 
        };
    
        setJdFromCompany();
      }, [formData.companyName]);

      const handleJobRoleSelect = (event) => {
        const selectedId = event.target.value;
        const selectedJobRole = jobRoles.find(jobRole => 
          jobRole._id === selectedId || jobRole.code === selectedId
        );
        
        if (selectedJobRole) {
          handleInputChange('jobRole', selectedJobRole);
          
          if (!jobRoleSpecific) {
            handleInputChange('companyName', '');
            setCompanies([]);
          }
        }
      }

      const handleCompanySelect = (event) => {
        const selectedCompanyId = event.target.value;
        const selectedCompany = companies.find(company => 
          company._id === selectedCompanyId || company.id === selectedCompanyId
        );
        
        if (selectedCompany) {
          handleInputChange('companyName', selectedCompany);
        }
      }

      const handleInputChange = (field, value) => {
        setFormData(prev => ({
          ...prev,
          [field]: value
        }))
      }

      const handleTextFieldChange = (event) => {
        const { name, value } = event.target;

        if (name === 'companyName') {
          handleInputChange('companyName', { name: value });
        } else if(name === 'jobRole'){
          handleInputChange('jobRole', {name: value});
        }else {
            handleInputChange(name , value);
        }
      }

      const handleNext = () => {
        if (currentStepIndex < stepFlow.length - 1) {
          setCurrentStep(stepFlow[currentStepIndex + 1]);
        }
      }

      const handleBack = () => {
        if (currentStepIndex > 0) {
          setCurrentStep(stepFlow[currentStepIndex - 1]);
        } else {
          onClose();
        }
      }

      const isNextDisabled = () => {
        switch (currentStep) {
          case STEPS.JOB_ROLE_COMPANY_SELECTION:
            if (jobRoleSpecific) {
              return detailsPresent ? !formData.jobRole : !formData.jobRole;
            } else {
              return !formData.jobRole || !formData.companyName;
            }
          case STEPS.JOB_DESCRIPTION:
            return !formData.jobDescription;
          default:
            return false;
        }
      }

      const getStepTitle = () => {
        switch (currentStep) {
          case STEPS.JOB_ROLE_COMPANY_SELECTION:
            return jobRoleSpecific 
            ? 'Create job role specific task' 
            : 'Create company specific task';
          case STEPS.JOB_DESCRIPTION:
            return 'Add Job Description';
          case STEPS.PUBLISH_TASK:
            return 'Publish Task';
          default:
            return jobRoleSpecific 
            ? 'Create job role specific task' 
            : 'Create company specific task';
        }
      }

      const getButtonText = () => {
        switch (currentStep) {
          case STEPS.JOB_ROLE_COMPANY_SELECTION:
            return jobRoleSpecific 
            ? 'Proceed' 
            : detailsPresent 
              ? 'Preview job description' 
              : 'Add job description';
          case STEPS.JOB_DESCRIPTION:
            return 'Proceed';
          case STEPS.PUBLISH_TASK:
            return 'Publish now';
          default:
            return 'Next';
        }
      }

        const handleFinish = async (values) => {
          try {
            setLoading(true)
            const payload = {
                name : values.name,
                user: Session.userId,
                batch: values.batch,
                jobRole: formData?.jobRole.name,
                publishOn: values.publishOn,
                expiresOn: values.expiresOn,
            };

            if (!jobRoleSpecific) {
              payload.company = formData?.companyName.name;
              payload.jobDescription = formData?.jobDescription;
            }

            const result = await service.callService(createShortlistTask, payload);
        
            if (result) {
              setRefresh(true);
              snackbar.success('Task created successfully!');
              setFormData({})
              setCurrentStep(STEPS.JOB_ROLE_COMPANY_SELECTION)
              onClose();
              setLoading(false)
            }
            
          } catch (error) {
            snackbar.error(error?.message || 'Failed to create task. Please try again.');
            console.error(error)
            setFormData({})
            setCurrentStep(STEPS.JOB_ROLE_COMPANY_SELECTION)
            onClose();
            setLoading(false)
          }
        };

      const renderStepContent = () => {
        switch (currentStep) {
          case STEPS.JOB_ROLE_COMPANY_SELECTION:
            return (
              <>
                {loading ? (
                  <AutoCompleteSelectSkeleton />
                ) :  (jobRoleSpecific || detailsPresent) ? (
                  <AutoCompleteSelect
                    name="selectJobRole"
                    options={jobRoles}
                    value={formData?.jobRole?._id || formData?.jobRole?.code || ''}
                    label="Job Role"
                    style={{ width: '100%' }}
                    onSelect={handleJobRoleSelect}
                    menuStyle={{ maxHeight: '200px' }}
                    width="100%"
                  />
                ) : (
                  <TextField
                    name="jobRole"
                    label="Job role"
                    placeholder="Job role"
                    background='#FFFFFF'
                    border='1px solid #636F7F'
                    width='100%'
                    value={formData?.jobRole?.name || ''}
                    onChange={handleTextFieldChange}
                  />
                )}

                {!jobRoleSpecific && detailsPresent && (
                  <>
                    {companiesLoading ? (
                      <AutoCompleteSelectSkeleton />
                    ) : (
                      <AutoCompleteSelect
                        name="selectCompany"
                        options={companies}
                        value={formData.companyName?._id || formData.companyName?.id || ''}
                        label="Company name"
                        style={{ width: '100%' }}
                        placeholder="Select Company"
                        onSelect={handleCompanySelect}
                        menuStyle={{ maxHeight: '200px' }}
                        width="100%"
                        disabled={!formData.jobRole}
                      />
                    )}
                  </>
                )}

                {!jobRoleSpecific && !detailsPresent && (
                  <TextField
                    name="companyName"
                    label="Company name"
                    placeholder="Company name"
                    background='#FFFFFF'
                    border='1px solid #636F7F'
                    width='100%'
                    value={formData.companyName?.name || ''}
                    onChange={handleTextFieldChange}
                  />
                )}
              </>
            );

          case STEPS.JOB_DESCRIPTION:
            return (
              <TextArea
                name="jobDescription"
                label={detailsPresent ? "Job description preview" : "Job description" }
                placeholder="Paste job description here..."
                background='#FFFFFF'
                border='1px solid #636F7F'
                width='100%'
                multiline
                rows={6}
                value={formData.jobDescription}
                onChange={handleTextFieldChange}
              />
            );

          case STEPS.PUBLISH_TASK:
            return (
              <PublishingComponent 
                onPublish={handleFinish}
                loading={loading}
              />
            );

          default:
            return null;
        }
      }

  return (
        <Dialog 
          open={open} 
          onClose={onClose}  
          maxWidth='700px' 
          width='100%'  
          scroll="paper"
          sx={{
            '& .MuiBackdrop-root': {
               backgroundColor: 'rgba(0, 17, 38, 0.7)',
               backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
            },
          }}
        >
          <Box display='flex' flexDirection='column'>
            <Box className={classes.container}>
              <Box className={classes.box}>
                <Header onBack={handleBack} title={getStepTitle()} />

                <Box display='flex' flexDirection='column' gap='16px'>
                  {renderStepContent()}
                  
                  <CustomButton 
                    onClick={currentStep === STEPS.PUBLISH_TASK ? undefined : handleNext}
                    type={currentStep === STEPS.PUBLISH_TASK ? "submit" : "button"}
                    form={currentStep === STEPS.PUBLISH_TASK ? "publish-form" : undefined}
                    disabled={currentStep === STEPS.PUBLISH_TASK ? false : isNextDisabled()}
                    style={{ display: currentStep === STEPS.PUBLISH_TASK ? 'none' : 'block' }}
                    size='medium'
                  >
                    {getButtonText()}
                  </CustomButton>
                  {!jobRoleSpecific && currentStep == STEPS.JOB_ROLE_COMPANY_SELECTION && (
                    <Typography 
                      variant='body01-link' 
                      color='primary.clr-400' 
                      sx={{textAlign : 'center' , cursor : 'pointer'}}
                      onClick={() => {
                        setFormData({})
                        setDetailsPresent(!detailsPresent)
                      }}
                    >
                      {detailsPresent ? 'Didn’t find the company or job role you are looking for? Click here' 
                      : 'Want to select from existing company & job role? Click here'}
                      
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>   
          </Box>
        </Dialog>
  )
}

export default CreateShortListTask